/**
 * Presentation helpers for the admin consoles. Every value rendered on these
 * screens comes from an API field; these functions only *format* those fields
 * (compact counts, dates, health/status mappings) — they never invent numbers.
 */
import type { DocStatus } from '../../components';
import type { EntityStatus } from '../../api/schema';

/** Compact a count the way the design shows it: `41300 → "41.3k"`, `341000 → "341k"`. */
export function compact(n: number): string {
  if (!Number.isFinite(n)) return '0';
  const abs = Math.abs(n);
  if (abs < 1000) return String(Math.round(n));
  if (abs < 1_000_000) {
    const k = n / 1000;
    // One decimal under 100k, none at/above (matches "41.3k" and "341k").
    const s = Math.abs(k) < 100 ? k.toFixed(1) : String(Math.round(k));
    return `${s.replace(/\.0$/, '')}k`;
  }
  const m = n / 1_000_000;
  return `${(Math.abs(m) < 100 ? m.toFixed(1) : String(Math.round(m))).replace(/\.0$/, '')}M`;
}

/** Plain thousands-separated integer (queue depths, attempt counts). */
export function fmtInt(n: number): string {
  return new Intl.NumberFormat('en-GB').format(Math.round(n));
}

/** `"21 Jul, 06:12"` — the DLQ timestamp format from the design. */
export function fmtTimestamp(iso: string | null | undefined): string {
  if (iso == null) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  const date = new Intl.DateTimeFormat('en-GB', { day: 'numeric', month: 'short' }).format(d);
  const time = new Intl.DateTimeFormat('en-GB', { hour: '2-digit', minute: '2-digit', hour12: false }).format(d);
  return `${date}, ${time}`;
}

/** `"21 Jul 2026"` — a plain date (config version timestamps). */
export function fmtDate(iso: string | null | undefined): string {
  if (iso == null) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return new Intl.DateTimeFormat('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }).format(d);
}

export type HealthLevel = 'ok' | 'warn' | 'bad';

/** Normalise the overview's free-text `health` to the three dot levels. */
export function healthLevel(raw: string): HealthLevel {
  const v = raw.toLowerCase();
  if (v === 'bad' || v === 'critical' || v === 'error' || v === 'suspended') return 'bad';
  if (v === 'warn' || v === 'warning' || v === 'degraded') return 'warn';
  return 'ok';
}

/** The tenant-status pill label from the design (`Active` / `Trial` / `Suspended`). */
export function statusLabel(status: EntityStatus, plan: string): string {
  if (status === 'suspended') return 'Suspended';
  if (status === 'archived') return 'Archived';
  // A trial plan reads as "Trial" even while the row status is active.
  if (plan.toLowerCase() === 'trial') return 'Trial';
  return 'Active';
}

/** Map a tenant status to the shared StatusBadge vocabulary for a11y parity. */
export function statusTone(status: EntityStatus): DocStatus {
  if (status === 'suspended' || status === 'archived') return 'Failed';
  return 'Classified';
}

/** Title-case a plan slug for display (`standard → "Standard"`). */
export function planLabel(plan: string): string {
  if (plan.length === 0) return '—';
  return plan.charAt(0).toUpperCase() + plan.slice(1);
}

/** The tenant's first word, ellipsised by CSS — the mini-bar row label. */
export function firstWord(name: string): string {
  const trimmed = name.trim();
  const space = trimmed.indexOf(' ');
  return space === -1 ? trimmed : trimmed.slice(0, space);
}

/** Derive the branding-hostname placeholder from a tenant name. */
export function hostnameFor(name: string): string {
  const slug = name.toLowerCase().replace(/[^a-z0-9]/g, '');
  return `${slug || 'newco'}.abcdash.co.uk`;
}
